import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import Container from '@mui/material/Container';

import { Box } from '@vk-hr-tek/ui/Box';
import { useInject } from '@vk-hr-tek/core/ioc';
import { useQuery } from '@vk-hr-tek/core/query';
import { useIsDesktop } from '@vk-hr-tek/ui/hooks';
import { TokenService } from '@vk-hr-tek/core/http';
import { Logger } from '@vk-hr-tek/core/logger';

import { useDispatch, useSelector } from '@app/hooks';

import { ErrorBoundary } from '../error-boundary';
import { AuthRouterService, AuthCandidateIdService } from '../../auth/services';
import { useAuth } from '../../auth';
import { CandidateLoginInitDto } from '../../auth/dto/candidate';
import { WithTranslations } from '../WithTranslations';
import { CandidatePageLogo } from '../ui/CandidatePageLogo';
import { loadCandidateLogo, selectIsLogoLoaded } from '../slice';

import { useStyles } from './CandidateLayout.styles';

interface CandidateLayoutProps {
  children: JSX.Element;
}

export const CandidateLayout = ({ children }: CandidateLayoutProps) => {
  const dispatch = useDispatch();
  const isAuthenticated = useAuth();
  const location = useLocation();
  const authRouter = useInject(AuthRouterService);
  const candidateIdService = useInject(AuthCandidateIdService);
  const token = useInject<TokenService>(TokenService);
  const logger = useInject(Logger);
  const [{ invite }] = useQuery(CandidateLoginInitDto);
  const isDesktop = useIsDesktop();
  const { classes } = useStyles();

  const isLogoLoaded = useSelector(selectIsLogoLoaded);

  const candidateId = candidateIdService.get();

  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!isAuthenticated && !location.pathname.startsWith('/auth/')) {
      authRouter.redirectToCandidateLogin();
    }

    if (
      isAuthenticated &&
      location.pathname.startsWith('/auth/login') &&
      invite &&
      candidateIdService.get() &&
      candidateIdService.get() !== invite
    ) {
      candidateIdService.delete();
      token.delete();
      setVersion((prevVersion) => prevVersion + 1);
    }
  }, [
    isAuthenticated,
    location,
    invite,
    authRouter,
    candidateIdService,
    token,
  ]);

  useEffect(() => {
    dispatch(loadCandidateLogo({ id: candidateId }));
  }, [dispatch, candidateId]);

  if (
    isAuthenticated &&
    location.pathname.startsWith('/auth/login') &&
    invite &&
    candidateIdService.get() &&
    candidateIdService.get() !== invite
  ) {
    return null;
  }

  return (
    <ErrorBoundary logger={logger}>
      <div className={isDesktop ? classes.root : classes.rootMobile}>
        {isAuthenticated ? (
          <>
            <header
              className={
                isLogoLoaded ? classes.headerWithoutBackground : classes.header
              }
            >
              <Container>
                <Box className={classes.logoContainer}>
                  <CandidatePageLogo
                    candidateEventId={candidateId}
                    variant={isDesktop ? '56' : '24'}
                  />
                </Box>
              </Container>
            </header>
            <Container className={classes.container}>
              <WithTranslations>{children}</WithTranslations>
            </Container>
          </>
        ) : (
          <WithTranslations>{children}</WithTranslations>
        )}
      </div>
    </ErrorBoundary>
  );
};
